import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { concat, forEach, isEmpty, isNil } from 'lodash';
import { NavPath } from 'src/pages/NavigationShell/navigationUtils';

export interface CrumbPath {
  /**
   * generated unique identifier,
   * this will help maintain order in case of duplicate paths names (self linking)
   * */
  id: string;
  /** original path */
  path: NavPath;
  queryParams: string;
  url: string;
}
export interface BreadcrumbState {
  crumbPaths: Record<string, CrumbPath[]>;
  activeBreadcrumb: string | null;
}

export interface AddBreadcrumbPayload {
  breadcrumbKey: string;
  currentCrumbPath: CrumbPath;
  nextCrumbPath: CrumbPath;
}

const initialState: BreadcrumbState = {
  crumbPaths: {},
  activeBreadcrumb: null,
};

const breadcrumbsSlice = createSlice({
  name: 'breadcrumbs',
  initialState: initialState,
  reducers: {
    addCrumb: (state, action: PayloadAction<AddBreadcrumbPayload>) => {
      const { breadcrumbKey, currentCrumbPath, nextCrumbPath } = action.payload;
      const existingCrumbPaths = state.crumbPaths[breadcrumbKey] ?? [];

      // when necessary, add current path (starting point of breadcrumbs) to existing paths
      let nextCrumbPaths: CrumbPath[] = isEmpty(existingCrumbPaths) ? [currentCrumbPath] : existingCrumbPaths;
      nextCrumbPaths = concat(nextCrumbPaths, [nextCrumbPath]);

      state.activeBreadcrumb = breadcrumbKey;
      state.crumbPaths[breadcrumbKey] = nextCrumbPaths;
    },
    removeCrumbById: (state, action: PayloadAction<string>) => {
      const activeCrumb = state.activeBreadcrumb;
      if (!isNil(activeCrumb)) {
        const idToRemove = action.payload;
        const crumbPaths = state.crumbPaths[activeCrumb];
        // find the navPath that contains any part of the selected path
        const pathIndex = crumbPaths.findIndex((cpo) => cpo.id === idToRemove);

        // remove items after current path in array
        const nextPaths = crumbPaths.slice(0, pathIndex + 1);
        const needsReset = nextPaths.length == 0 || nextPaths.length == 1;
        if (needsReset) {
          // back on root crumb, reset state all the way
          state.crumbPaths[activeCrumb] = [];
          state.activeBreadcrumb = null;
        } else {
          state.crumbPaths[activeCrumb] = nextPaths;
        }
      }
    },
    removeLastCrumb: (state) => {
      const activeCrumb = state.activeBreadcrumb;

      if (!isNil(activeCrumb)) {
        const nextPaths = state.crumbPaths[activeCrumb];
        nextPaths.pop();
        if (nextPaths.length === 1) {
          // back on root crumb, reset state all the way
          state.crumbPaths[activeCrumb] = [];
          state.activeBreadcrumb = null;
        } else {
          state.crumbPaths[activeCrumb] = nextPaths;
        }
      }
    },
    resetCrumbsByKey: (state, action: PayloadAction<string>) => {
      state.crumbPaths[action.payload] = [];
      state.activeBreadcrumb = null;
    },
    resetAllCrumbs: (state) => {
      forEach(state.crumbPaths, (_, crumbKey) => {
        state.crumbPaths[crumbKey] = [];
      });
      state.activeBreadcrumb = null;
    },
  },
});

export const { addCrumb, removeCrumbById, removeLastCrumb, resetAllCrumbs, resetCrumbsByKey } =
  breadcrumbsSlice.actions;

export default breadcrumbsSlice.reducer;
